import cx from 'clsx';
import React from 'react';

import ButtonCopyDetails from '@components/common/ButtonCopyDetails/ButtonCopyDetails';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';

import useLiveTranslate from '@pages/ActionItem/hooks/useLiveTranslate';

import { selectAppPersistOnBoardStart } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import TranslateBtn from '../TranslateBtn/TranslateBtn';
import DescriptionSkeleton from './DescriptionSkeleton';
import style from './style.module.scss';

interface IProps {
  actionItem?: IActionItem;
  chartImages?: Array<{ src: string }>;
}

const Description: React.FC<IProps> = ({ actionItem, chartImages }) => {
  const { t } = useTranslation();
  const { setActiveModal } = useModalActions();

  const onBoard = selectAppPersistOnBoardStart();

  const { hasTranslate, translates, isTranslated, isTranslateLoading, toggleIsTranslate } = useLiveTranslate({
    item: actionItem,
    fields: ['description'],
    entityId: actionItem?.id,
  });

  const isLoading = !actionItem || !chartImages;

  if (isLoading) return <DescriptionSkeleton />;

  const handleOpenLightbox = (event: React.MouseEvent<HTMLElement>): void => {
    const target = event.target;

    if (!(target instanceof HTMLImageElement)) return;

    let images = actionItem.description.match(/<img [^>]*src="[^"]*"[^>]*>/gm)!.map((x) => {
      return { src: x.replace(/.*src="([^"]*)".*/, '$1') };
    });

    if (chartImages?.length) {
      images = [...images, ...chartImages];
    }

    if (images.length) {
      const index = images.findIndex((image) => image.src === target?.currentSrc);

      setActiveModal({ name: MODAL_NAMES.lightboxModal, options: { images, index } });
    }
  };

  return (
    <div className={style.block}>
      <div className={style.blockHead}>
        <div className={style.blockTitle} id="tourStep-14">
          {t('What We are Seeing')}
          <ButtonCopyDetails
            id="copyDescription"
            copyDetailsData={[actionItem?.descriptionInfo]}
            tooltipInfo={t('Copy description.')}
          />
        </div>
        <TranslateBtn
          isTranslated={isTranslated}
          hasTranslate={hasTranslate}
          isTranslateLoading={isTranslateLoading}
          toggleIsTranslate={toggleIsTranslate}
        />
      </div>
      <div className={cx(style.blockContainer, { [style.onBoardBlockHide]: onBoard })}>
        <div
          className={style.descriptionInfo}
          dangerouslySetInnerHTML={{
            __html: translates['description'],
          }}
          onClick={(event) => handleOpenLightbox(event)}
        />
        {!!chartImages?.length &&
          chartImages?.map((chart, i) => (
            <img className={style.chartImage} src={chart.src} alt={`chart-${i}`} />
          ))}
        {!!actionItem.chartImages?.length && (
          <div>
            {actionItem.chartImages.map((chartImage, index) => (
              <div className={style.descrImageWrapper} key={index} data-qa={index}>
                <img key={index} src={chartImage} alt="" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Description;
